import React from 'react';
import ReactDOM from 'react-dom';
import { IdsIcon } from '@investec/ids-web-react';
import { productTypes } from '../../../../utils/productHelpers';
import { useUnifiedAnalytics } from '../../../../contexts/AnalyticsWrapper';
import { InterestRateTooltip, MaturityTooltip, FundingWindowTooltip } from '../Tooltips';
import withFeatureFlags from '../../../FeatureFlags/withFeatureFlags';
import chevDown from '../../../../assets/images/chev-down-royal.svg';
import chevUp from '../../../../assets/images/chev-up.svg';

/**
 * Mobile Modal Portal - renders modal at body level to escape parent stacking contexts
 * @param {{ children: React.ReactNode, isOpen: boolean }} props
 */
const MobileModalPortal = ({ children, isOpen }) => {
    if (!isOpen) return null;
    
    // Only show as portal on mobile
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
        return ReactDOM.createPortal(
            children,
            document.body
        );
    }
    
    // On desktop, render inline (will be hidden by CSS)
    return <>{children}</>;
};

/**
 * Calculate funding time left from minutes
 * @param {number | undefined} fundingWindowMinutesLeft
 * @returns {string}
 */
function calculateFundingTimeLeft(fundingWindowMinutesLeft) {
    if (!fundingWindowMinutesLeft) return '-';

    let delta = fundingWindowMinutesLeft * 60;

    // calculate (and subtract) whole days
    const days = Math.floor(delta / 86400);
    delta -= days * 86400;

    // calculate (and subtract) whole hours
    const hours = Math.floor(delta / 3600) % 24;
    delta -= hours * 3600;

    // calculate (and subtract) whole minutes
    const minutes = Math.floor(delta / 60) % 60;

    // build up the FW string
    let dayString = '0 days, ';
    let hourString = '0 hours';
    let minutesString = '0 minutes';

    if (days > 0) {
        dayString = days === 1 ? '1 day ' : `${days} days, `;
    }

    if (hours > 0) {
        hourString = hours === 1 ? '1 hour' : `${hours} hours`;
    }

    if (minutes > 0) {
        minutesString = minutes === 1 ? '1 minute' : `${minutes} minutes`;
    }

    return `${dayString}${hourString} ${minutesString}`;
}

/**
 * Format sort code with dashes (xx-xx-xx)
 * @param {string} sortCode
 * @returns {string}
 */
function formatSortCode(sortCode) {
    if (!sortCode) return '';
    const cleaned = sortCode.replace(/\D/g, '');
    if (cleaned.length === 6) {
        return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 4)}-${cleaned.slice(4, 6)}`;
    }
    return sortCode;
}

/**
 * Format interest rate with 2 decimal places
 * @param {number | string} rate
 * @returns {string}
 */
function formatInterestRate(rate) {
    const numRate = typeof rate === 'string' ? parseFloat(rate) : rate;
    if (isNaN(numRate)) return '0.00';
    return numRate.toFixed(2);
}

/** Display labels for each transfer in status value
 * @type {Record<string, string>}
 */
const TRANSFER_STATUS_LABELS = {
    Requested:             'Requested',
    InProgress:            'In progress',
    Completed:             'Completed',
    Unsuccessful:          'Unsuccessful',
    CancellationRequested: 'Cancellation requested',
    Cancelled:             'Cancelled',
};

/** CSS modifier per status — avoids camelCase conversion issues (e.g. InProgress → in-progress)
 * @type {Record<string, string>}
 */
const TRANSFER_STATUS_MODIFIERS = {
    Requested:             'requested',
    InProgress:            'in-progress',
    Completed:             'completed',
    Unsuccessful:          'unsuccessful',
    CancellationRequested: 'cancellation-requested',
    Cancelled:             'cancelled',
};

/**
 * Normalize transfer status values from API/cache into the canonical keys used by the UI.
 * This handles variants like "Cancellation requested" and "CancellationRequested".
 * @param {string | undefined | null} status
 * @returns {string}
 */
function normalizeTransferStatus(status) {
    if (!status) return '';

    const collapsedStatus = String(status).trim().replace(/[\s_-]+/g, '').toLowerCase();
    /** @type {Record<string, string>} */
    const normalizedStatusMap = {
        requested: 'Requested',
        inprogress: 'InProgress',
        completed: 'Completed',
        unsuccessful: 'Unsuccessful',
        cancellationrequested: 'CancellationRequested',
        cancelled: 'Cancelled',
    };

    return normalizedStatusMap[collapsedStatus] || String(status).trim();
}

/**
 * @param {string | undefined | null} status
 * @returns {string}
 */
function getTransferStatusLabel(status) {
    const normalizedStatus = normalizeTransferStatus(status);
    return TRANSFER_STATUS_LABELS[normalizedStatus] || normalizedStatus;
}

/**
 * @param {string | undefined | null} status
 * @returns {string}
 */
function getTransferStatusModifier(status) {
    const normalizedStatus = normalizeTransferStatus(status);
    return TRANSFER_STATUS_MODIFIERS[normalizedStatus]
        || normalizedStatus.toLowerCase().replace(/[^a-z0-9]+/g, '-');
}

/**
 * Format a transfer in date string to "d Month yyyy"
 * @param {string} dateString
 * @returns {string}
 */
function formatTransferInDate(dateString) {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

/**
 * Pick the most recently updated ISA transfer request for account-card display.
 * @param {{ transferIn?: { lastUpdatedAt?: string }, transferOut?: { lastUpdatedAt?: string } }} account
 * @returns {{ direction: 'in' | 'out', transfer: any } | null}
 */
function getLatestAccountTransfer(account) {
    /** @type {Array<{ direction: 'in' | 'out', transfer: any, updatedAt: number }>} */
    const candidates = /** @type {any} */ ([
        account?.transferIn && {
            direction: 'in',
            transfer: account.transferIn,
            updatedAt: Date.parse(account.transferIn.lastUpdatedAt || '')
        },
        account?.transferOut && {
            direction: 'out',
            transfer: account.transferOut,
            updatedAt: Date.parse(account.transferOut.lastUpdatedAt || '')
        }
    ].filter(Boolean));

    if (!candidates.length) {
        return null;
    }

    const finalStatuses = new Set(['Completed', 'Unsuccessful', 'Cancelled']);
    const getUkCalendarDay = (dateValue) => {
        const date = new Date(dateValue);
        if (Number.isNaN(date.getTime())) return null;

        const parts = new Intl.DateTimeFormat('en-GB', {
            timeZone: 'Europe/London',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        }).formatToParts(date);
        const part = (type) => parts.find((item) => item.type === type)?.value;
        return Date.UTC(Number(part('year')), Number(part('month')) - 1, Number(part('day'))) / 86400000;
    };
    const today = getUkCalendarDay(Date.now());
    const latestTransfer = candidates
        .sort((left, right) => {
            const leftUpdatedAt = Number.isNaN(left?.updatedAt) ? 0 : left.updatedAt;
            const rightUpdatedAt = Number.isNaN(right?.updatedAt) ? 0 : right.updatedAt;

            if (rightUpdatedAt !== leftUpdatedAt) {
                return rightUpdatedAt - leftUpdatedAt;
            }

            return left?.direction === 'out' ? -1 : 1;
        })[0] ?? null;

    if (!latestTransfer || !finalStatuses.has(normalizeTransferStatus(latestTransfer.transfer.status))) {
        return latestTransfer;
    }

    const finalDay = getUkCalendarDay(latestTransfer.transfer.lastUpdatedAt);
    return finalDay !== null && today - finalDay < 14 ? latestTransfer : null;
}

/**
 * Account Card Front Face component
 * @param {{ 
 *   account: any;
 *   customer: any;
 *   isClosed: boolean;
 *   isClosureRequested: boolean;
 *   activityIsShowing: boolean;
 *   actionButton: React.ReactNode;
 *   onToggleActivity: () => void;
 *   onInfoIconClick: (event: React.MouseEvent, accountId: string) => void;
 *   onMaturityInfoIconClick: (event: React.MouseEvent, accountId: string) => void;
 *   onAvailableBalanceIconClick: (event: React.MouseEvent, accountId: string) => void;
 *   onFundingInfoIconClick: (event: React.MouseEvent, accountId: string) => void;
 *   onRateChangeInfoIconClick: (event: React.MouseEvent, accountId: string) => void;
 *   openTooltipId: string | null;
 *   openMaturityTooltipId: string | null;
 *   openFundingTooltipId: string | null;
 *   openRateChangeTooltipId: string | null;
 *   featureEnabled: (flag: string) => boolean;
 * }} props
 */
const AccountCardFront = ({
    account,
    customer,
    hasDisaAccountAgainstFisa,
    isClosed,
    isClosureRequested,
    activityIsShowing,
    actionButton,
    onToggleActivity,
    onInfoIconClick,
    onMaturityInfoIconClick,
    onAvailableBalanceIconClick,
    onFundingInfoIconClick,
    onRateChangeInfoIconClick,
    openTooltipId,
    openMaturityTooltipId,
    openFundingTooltipId,
    openRateChangeTooltipId,
    featureEnabled
}) => {
    const analytics = useUnifiedAnalytics();
    const productType = account.product.productType;
    const isFisaMaturityEnabled = featureEnabled && featureEnabled('EnableFisaMaturity');
    
    // Rate change notification - only show if feature is enabled and account is not closed/closure requested
    const isRateChangeEnabled = featureEnabled('EnableInterimRateDecrease');
    const isAccountEligibleForRateChange = account.accountStatus !== 'ClosureRequested' && account.accountStatus !== 'Closed';
    const hasRateChanges = account.product.rateChanges && account.product.rateChanges.length > 0;
    const shouldShowRateChange = isRateChangeEnabled && isAccountEligibleForRateChange && hasRateChanges;
    const rateChangeList = hasRateChanges ? account.product.rateChanges : [];
    const latestTransfer = getLatestAccountTransfer(account);
    const latestTransferIsIn = latestTransfer?.direction === 'in';

    const shouldShowClosedFisaTransferInfo = isFisaMaturityEnabled && isClosed && productType === productTypes.FixedRateCashISA && Boolean(hasDisaAccountAgainstFisa);
    const closedFisaTransferDate = account.maturity?.maturityDate
        ? formatTransferInDate(account.maturity.maturityDate)
        : '';
    
    /**
     * @param {React.MouseEvent<HTMLElement>} e - Click event
     */
    const handleRateChangeClick = (e) => {
        // Track analytics
        if (analytics) {
            analytics.trackAnalyticsClickEvent(
                'rate change',
                window.location.href,
                'rate notification',
                account?.product.name,
                '',
                {},
                openRateChangeTooltipId === account.id ? 'hide' : 'reveal',
                'dashboard|click|show-rate-change',
                'dashboard'
            );
        }
        
        onRateChangeInfoIconClick(e, account.id);
    };
    
    /**
     * @param {string} dateString - Date string to format
     */
    const formatRateChangeDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    };
    
    // Funding window info for accounts in FundingOpen status
    const isFundingOpen = account.accountStatus === 'FundingOpen';
    const fundingTimeLeft = isFundingOpen ? calculateFundingTimeLeft(account.fundingWindowMinutesLeft) : null;

    // Maturing account — instruction window is open and account is not closed
    const isMaturing = !isClosed &&
        account.maturity &&
        account.maturity.maturityInstructionWindowStatus === 'InstructionOpen' &&
        (productType !== productTypes.FixedRateCashISA || isFisaMaturityEnabled);
    
    // Maturity info for Fixed Term accounts
    let maturityInfoJSX = null;
    let hasMaturityInfo = false;
    
    if (!isClosed && account.maturity && account.maturity.maturityDate) {
        const maturityDate = new Date(account.maturity.maturityDate);
        const formattedDate = maturityDate.toLocaleDateString('en-GB', { 
            day: 'numeric', 
            month: 'long', 
            year: 'numeric' 
        });
        hasMaturityInfo = true;
        maturityInfoJSX = (
            <span 
                className="maturity-info-clickable"
                onClick={(e) => onMaturityInfoIconClick(e, account.id)}
            >
                Your term ends on {formattedDate}
                <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    width="17" 
                    height="17" 
                    viewBox="0 0 17 17" 
                    fill="none" 
                    className="info-icon-inline"
                >
                    <circle cx="8.5" cy="8.5" r="8.5" fill="#286B6E"/>
                    <path d="M8.95858 6.85267C8.95858 6.60595 8.75148 6.41406 8.48521 6.41406H6V7.26386H8.04142V11.8144H6V12.6642H11V11.8144H8.95858V6.85267Z" fill="white" stroke="white" strokeWidth="0.35"/>
                    <path d="M8.48132 4.64471C8.9547 4.64471 9.3689 4.28836 9.3689 3.82236C9.3689 3.35635 8.98428 3 8.48132 3C7.97836 3 7.59375 3.35635 7.59375 3.82236C7.62334 4.28836 8.00795 4.64471 8.48132 4.64471Z" fill="white" stroke="white" strokeWidth="0.35"/>
                </svg>
            </span>
        );
    }

    const showInterestRateTooltip = (productType === productTypes.EasyAccess || productType === productTypes.InstantAccessCashISA || productType === productTypes.Notice) &&
        account.product.interestRateTiers && 
        account.product.interestRateTiers.length > 0;

    const showAvailableBalance = productType === productTypes.Notice && 
        account.balance !== account.availableBalance;

    return (
        <div className={`account-card ${isClosed ? 'account-card--closed' : ''}`}>
            <div className="account-card__main">
                <div className="account-card__title-row">
                    <h4 className="account-card__title">
                        {account.product.name}
                        {productType === productTypes.Notice && typeof account.product.issueNumber === 'number' && (
                            <span className="account-card__title--issue"> Issue {account.product.issueNumber}</span>
                        )}
                        {isMaturing && (
                            <span className="account-card__title--maturing"> (maturing)</span>
                        )}
                    </h4>
                    {isClosed && (
                        <span className="account-card__closed-status">
                            {isClosureRequested ? 'CLOSURE IN PROGRESS' : 'CLOSED'}
                        </span>
                    )}
                </div>
                <div className="account-card__content">
                    <div className="account-card__info-columns">
                        <div className="account-card__info-column account-card__info-column--left">
                            <div className="account-card__info-item account-card__info-item--account-no">
                                <span className="account-card__label">Account no.</span>
                                <span className="account-card__value">{account.accountNumber}</span>
                            </div>
                            <div className="account-card__info-item account-card__info-item--account-holder">
                                <span className="account-card__label">Account holder</span>
                                <span className="account-card__value">{customer.firstName} {customer.middleName} {customer.lastName}</span>
                            </div>
                        </div>
                        <div className="account-card__info-column account-card__info-column--middle">
                            <div className="account-card__info-item account-card__info-item--sort-code">
                                <span className="account-card__label">Sort code</span>
                                <span className="account-card__value">{formatSortCode(account.sortCode)}</span>
                            </div>
                            <div className="account-card__info-item account-card__info-item--interest-rate">
                                <span className="account-card__label">Interest rate</span>
                                <span className="account-card__value">
                                    {formatInterestRate(account.product.interestRate)}%
                                    {showInterestRateTooltip && (
                                        <span className="interest-rate-tooltip-wrapper">
                                            <svg 
                                                xmlns="http://www.w3.org/2000/svg" 
                                                width="17" 
                                                height="17" 
                                                viewBox="0 0 17 17" 
                                                fill="none" 
                                                className="interest-rate-info-icon"
                                                onClick={(e) => onInfoIconClick(e, account.id)}
                                            >
                                                <circle cx="8.5" cy="8.5" r="8.5" fill="#286B6E"/>
                                                <path d="M8.95858 6.85267C8.95858 6.60595 8.75148 6.41406 8.48521 6.41406H6V7.26386H8.04142V11.8144H6V12.6642H11V11.8144H8.95858V6.85267Z" fill="white" stroke="white" strokeWidth="0.35"/>
                                                <path d="M8.48132 4.64471C8.9547 4.64471 9.3689 4.28836 9.3689 3.82236C9.3689 3.35635 8.98428 3 8.48132 3C7.97836 3 7.59375 3.35635 7.59375 3.82236C7.62334 4.28836 8.00795 4.64471 8.48132 4.64471Z" fill="white" stroke="white" strokeWidth="0.35"/>
                                            </svg>
                                        </span>
                                    )}
                                </span>
                            </div>
                        </div>
                    </div>
                    
                    {/* Inline interest rate tooltip for mobile */}
                    <MobileModalPortal isOpen={showInterestRateTooltip && openTooltipId === account.id}>
                        <div className="tooltip-backdrop" onClick={(e) => onInfoIconClick(e, account.id)} />
                        <div 
                            className="interest-rate-tooltip-inline" 
                            onClick={(e) => e.stopPropagation()}
                            onTouchEnd={(e) => e.stopPropagation()}
                        >
                            <button 
                                className="tooltip-close-btn"
                                onClick={(e) => onInfoIconClick(e, account.id)}
                                aria-label="Close"
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M18 6L6 18" stroke="#30384a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M6 6L18 18" stroke="#30384a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                            <InterestRateTooltip account={account} />
                        </div>
                    </MobileModalPortal>
                    
                    <div className="account-card__balance">
                        £{account.balance.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    
                    {showAvailableBalance && (
                        <div className="account-card__available-balance">
                            <span className="account-card__available-balance-text">
                                £{account.availableBalance.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} available
                            </span>
                            <svg 
                                xmlns="http://www.w3.org/2000/svg" 
                                width="17" 
                                height="17" 
                                viewBox="0 0 17 17" 
                                fill="none" 
                                className="account-card__available-balance-icon"
                                onClick={(e) => onAvailableBalanceIconClick(e, account.id)}
                            >
                                <circle cx="8.5" cy="8.5" r="8.5" fill="#286B6E"/>
                                <path d="M8.95858 6.85267C8.95858 6.60595 8.75148 6.41406 8.48521 6.41406H6V7.26386H8.04142V11.8144H6V12.6642H11V11.8144H8.95858V6.85267Z" fill="white" stroke="white" strokeWidth="0.35"/>
                                <path d="M8.48132 4.64471C8.9547 4.64471 9.3689 4.28836 9.3689 3.82236C9.3689 3.35635 8.98428 3 8.48132 3C7.97836 3 7.59375 3.35635 7.59375 3.82236C7.62334 4.28836 8.00795 4.64471 8.48132 4.64471Z" fill="white" stroke="white" strokeWidth="0.35"/>
                            </svg>
                            {openTooltipId === `${account.id}-available` && (
                                <div className="account-card__available-balance-tooltip-inline">
                                    <h3>What's the available balance?</h3>
                                    <ul>
                                        <li>This is the portion of your balance that isn't already scheduled for withdrawal.</li>
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            
            <div className={`account-card__actions ${hasMaturityInfo || shouldShowClosedFisaTransferInfo || isFundingOpen || shouldShowRateChange ? 'account-card__actions--with-info' : ''} ${shouldShowClosedFisaTransferInfo ? 'account-card__actions--closed-fisa-transfer' : ''}`}>
                <div className="account-card__bottom-row">
                    {/* Funding Window Info - shown when account is in FundingOpen status */}
                    {isFundingOpen && (
                        <div className="account-card__info-row">
                            <div 
                                className="account-card__info-icon"
                                onClick={(e) => onFundingInfoIconClick(e, account.id)}
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="12" cy="12" r="9" stroke="#1463AA" strokeWidth="2"/>
                                    <path d="M12 8V12" stroke="#1463AA" strokeWidth="2" strokeLinecap="round"/>
                                    <circle cx="12" cy="16" r="0.5" fill="#1463AA" stroke="#1463AA"/>
                                </svg>
                            </div>
                            <span 
                                className="account-card__info-text funding-info-clickable"
                                onClick={(e) => onFundingInfoIconClick(e, account.id)}
                            >
                                Time left to add funds {fundingTimeLeft}
                                <svg 
                                    xmlns="http://www.w3.org/2000/svg" 
                                    width="17" 
                                    height="17" 
                                    viewBox="0 0 17 17" 
                                    fill="none" 
                                    className="info-icon-inline"
                                >
                                    <circle cx="8.5" cy="8.5" r="8.5" fill="#286B6E"/>
                                    <path d="M8.95858 6.85267C8.95858 6.60595 8.75148 6.41406 8.48521 6.41406H6V7.26386H8.04142V11.8144H6V12.6642H11V11.8144H8.95858V6.85267Z" fill="white" stroke="white" strokeWidth="0.35"/>
                                    <path d="M8.48132 4.64471C8.9547 4.64471 9.3689 4.28836 9.3689 3.82236C9.3689 3.35635 8.98428 3 8.48132 3C7.97836 3 7.59375 3.35635 7.59375 3.82236C7.62334 4.28836 8.00795 4.64471 8.48132 4.64471Z" fill="white" stroke="white" strokeWidth="0.35"/>
                                </svg>
                            </span>
                            <MobileModalPortal isOpen={openFundingTooltipId === account.id}>
                                <div className="tooltip-backdrop" onClick={(e) => onFundingInfoIconClick(e, account.id)} />
                                <div 
                                    className="funding-info-tooltip-inline" 
                                    onClick={(e) => e.stopPropagation()}
                                    onTouchEnd={(e) => e.stopPropagation()}
                                >
                                    <button 
                                        className="tooltip-close-btn"
                                        onClick={(e) => onFundingInfoIconClick(e, account.id)}
                                        aria-label="Close"
                                    >
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M18 6L6 18" stroke="#30384a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M6 6L18 18" stroke="#30384a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </button>
                                    <FundingWindowTooltip productType={productType} />
                                </div>
                            </MobileModalPortal>
                        </div>
                    )}
                    
                    {/* Maturity Info - shown when account has maturity date */}
                    {maturityInfoJSX && !isFundingOpen && !shouldShowRateChange && (
                        <div className="account-card__info-row">
                            <div 
                                className="account-card__info-icon" 
                                onClick={(e) => onMaturityInfoIconClick(e, account.id)}
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="12" cy="12" r="9" stroke="#1463AA" strokeWidth="2"/>
                                    <path d="M12 8V12" stroke="#1463AA" strokeWidth="2" strokeLinecap="round"/>
                                    <circle cx="12" cy="16" r="0.5" fill="#1463AA" stroke="#1463AA"/>
                                </svg>
                            </div>
                            <span className="account-card__info-text">{maturityInfoJSX}</span>
                            <MobileModalPortal isOpen={openMaturityTooltipId === account.id}>
                                <div className="tooltip-backdrop" onClick={(e) => onMaturityInfoIconClick(e, account.id)} />
                                <div 
                                    className="maturity-info-tooltip-inline" 
                                    onClick={(e) => e.stopPropagation()}
                                    onTouchEnd={(e) => e.stopPropagation()}
                                >
                                    <button 
                                        className="tooltip-close-btn"
                                        onClick={(e) => onMaturityInfoIconClick(e, account.id)}
                                        aria-label="Close"
                                    >
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M18 6L6 18" stroke="#30384a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M6 6L18 18" stroke="#30384a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </button>
                                    <MaturityTooltip
                                        isFisa={account.product.productType === productTypes.FixedRateCashISA}
                                        enableUpdatedFisaCopy={featureEnabled('EnableDashboardMaturityCopyUpdate')}
                                    />
                                </div>
                            </MobileModalPortal>
                        </div>
                    )}

                    {/* Closed FISA maturity transfer info */}
                    {shouldShowClosedFisaTransferInfo && !isFundingOpen && !shouldShowRateChange && (
                        <div className="account-card__info-row account-card__info-row--closed-fisa-transfer">
                            <span className="account-card__value">
                                <IdsIcon
                                    name="information"
                                    size="md"
                                    alt=""
                                    aria-hidden="true"
                                    style={{ '--idsIcon-color': 'var(--ids-color-icon-accent-9, currentColor)' }}
                                />
                                <span className="account-card__closed-fisa-transfer-message">
                                    Transferred to an Instant Access Cash ISA{closedFisaTransferDate ? ` on ${closedFisaTransferDate}` : ''}
                                </span>
                            </span>
                        </div>
                    )}

                    {isClosed && (
                        <span className="account-card__closed-status">
                            {isClosureRequested ? 'CLOSURE IN PROGRESS' : 'CLOSED'}
                        </span>
                    )}
                    
                    {/* Rate Decrease Notification - Shows same position as maturity notification */}
                    {shouldShowRateChange && !isFundingOpen && (
                        <div className="account-card__info-row">
                            <div 
                                className="account-card__info-icon"
                                onClick={handleRateChangeClick}
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="12" cy="12" r="9" stroke="#1463AA" strokeWidth="2"/>
                                    <path d="M12 8V12" stroke="#1463AA" strokeWidth="2" strokeLinecap="round"/>
                                    <circle cx="12" cy="16" r="0.5" fill="#1463AA" stroke="#1463AA"/>
                                </svg>
                            </div>
                            <span 
                                className="account-card__info-text rate-change-info-clickable"
                                onClick={handleRateChangeClick}
                            >
                                Your savings rate is decreasing. <span className="view-changes-link">View changes</span>
                            </span>
                            <MobileModalPortal isOpen={openRateChangeTooltipId === account.id}>
                                <div className="tooltip-backdrop" onClick={handleRateChangeClick} />
                                <div 
                                    className="rate-change-info-tooltip-inline" 
                                    onClick={(e) => e.stopPropagation()}
                                    onTouchEnd={(e) => e.stopPropagation()}
                                >
                                        <button 
                                            className="rate-change-close-btn"
                                            onClick={handleRateChangeClick}
                                            aria-label="Close"
                                        >
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M18 6L6 18" stroke="#30384a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                <path d="M6 6L18 18" stroke="#30384a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </button>
                                        <h3>Rate change details</h3>
                                    <p className="rate-change-message">
                                        You don't need to do anything, the new interest rate will be applied automatically.
                                    </p>
                                    {rateChangeList.map((/** @type {any} */ rateChange, /** @type {number} */ index) => (
                                        <div key={index} className="rate-change-item">
                                            <div className="rate-change-details">
                                                <strong>New rate:</strong> {rateChange.aerRate.toFixed(2)}% AER* | {rateChange.rate.toFixed(2)}% gross p.a.
                                            </div>
                                            <div className="rate-change-details">
                                                <strong>Effective from:</strong> {formatRateChangeDate(rateChange.scheduledFor)}
                                            </div>
                                        </div>
                                    ))}
                                    <p className="rate-change-footnote">
                                        You'll earn the applicable interest rate as of the effective date specified above on your entire balance when it's between £5,000 - £250,000.
                                    </p>
                                </div>
                            </MobileModalPortal>
                        </div>
                    )}
                    
                    {/* Transfer Status - show only the latest transfer request */}
                    {latestTransfer && (
                        <div className="account-card__transfer-in-block">
                        <hr className="account-card__transfer-in-divider" />
                        <div className="account-card__info-row account-card__info-row--transfer-in">
                            <div className="account-card__transfer-in-label">
                                {latestTransferIsIn ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" className="account-card__transfer-in-icon" aria-hidden="true">
                                        <path d="M12 4C12.5523 4 13 4.44772 13 5V16.5858L18.2929 11.2929C18.6834 10.9024 19.3166 10.9024 19.7071 11.2929C20.0976 11.6834 20.0976 12.3166 19.7071 12.7071L12.7071 19.7071C12.3166 20.0976 11.6834 20.0976 11.2929 19.7071L4.29289 12.7071C3.90237 12.3166 3.90237 11.6834 4.29289 11.2929C4.68342 10.9024 5.31658 10.9024 5.70711 11.2929L11 16.5858V5C11 4.44772 11.4477 4 12 4Z" fill="#489759"/>
                                    </svg>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" className="account-card__transfer-in-icon" aria-hidden="true">
                                        <path d="M12 20C11.4477 20 11 19.5523 11 19V7.41421L5.70711 12.7071C5.31658 13.0976 4.68342 13.0976 4.29289 12.7071C3.90237 12.3166 3.90237 11.6834 4.29289 11.2929L11.2929 4.29289C11.6834 3.90237 12.3166 3.90237 12.7071 4.29289L19.7071 11.2929C20.0976 11.6834 20.0976 12.3166 19.7071 12.7071C19.3166 13.0976 18.6834 13.0976 18.2929 12.7071L13 7.41421V19C13 19.5523 12.5523 20 12 20Z" fill="#B3393F"/>
                                    </svg>
                                )}
                                <span className="account-card__transfer-in-text">{latestTransferIsIn ? 'Transfer in' : 'Transfer Out'}</span>
                            </div>
                            <div className="account-card__transfer-in-row2">
                                <span className={`account-card__transfer-in-tag account-card__transfer-in-tag--${getTransferStatusModifier(latestTransfer.transfer.status)}`}>
                                    {getTransferStatusLabel(latestTransfer.transfer.status)}
                                </span>
                                <span className="account-card__transfer-in-date">
                                    <span className="account-card__transfer-in-date-prefix">last updated</span>
                                    {' '}
                                    <span className="account-card__transfer-in-date-value">{formatTransferInDate(latestTransfer.transfer.lastUpdatedAt)}</span>
                                </span>
                            </div>
                        </div>
                        <hr className="account-card__transfer-in-divider" />
                        </div>
                    )}

                    <div className="account-card__action-controls">
                        <div className="account-card__buttons">
                            <button
                                className="button button--tertiary button--sml"
                                onClick={onToggleActivity}
                            >
                                <span>Account activity</span>
                                {activityIsShowing ? (
                                    <img className="account-card__button-icon" src={chevUp} alt="" />
                                ) : (
                                    <img className="account-card__button-icon" src={chevDown} alt="" />
                                )}
                            </button>
                        </div>
                        <div className="account-card__primary-actions">
                            {actionButton}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default withFeatureFlags(AccountCardFront);
